import React, { useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { X, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Standardized modal component with blur backdrop and optional draft persistence
 * Sizes: sm (400px), md (600px), lg (800px), xl (90vh scrollable)
 */
const StandardModal = ({ 
  isOpen, 
  onClose, 
  title, 
  description,
  children,
  footer,
  size = 'md', // sm, md, lg, xl
  showCloseButton = true,
  closeOnOutsideClick = true,
  persistKey, // If provided, will show draft indicator
  hasDraft = false,
  dataTestId
}) => {
  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    lg: 'max-w-4xl',
    xl: 'max-w-6xl'
  };

  // Radix can leave body pointer-events:none when stacked dialogs swap quickly —
  // force-clear it after close so the page never gets stuck unclickable.
  useEffect(() => {
    if (!isOpen) {
      const t = setTimeout(() => {
        if (document.body.style.pointerEvents === 'none') document.body.style.pointerEvents = '';
      }, 350);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  return (
    <Dialog 
      open={isOpen} 
      onOpenChange={(open) => {
        if (!open && closeOnOutsideClick) {
          onClose();
        }
      }}
    >
      <DialogContent
        data-testid={dataTestId}
        className={`${sizeClasses[size]} flex flex-col`}
        onPointerDownOutside={(e) => { if (window.__bfTourActive) e.preventDefault(); }}
        onInteractOutside={(e) => { if (window.__bfTourActive) e.preventDefault(); }}
      >
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <DialogTitle>{title}</DialogTitle>
                {hasDraft && (
                  <span className="badge-app badge-app-info">
                    Draft Restored
                  </span>
                )}
              </div>
              {description
                ? <DialogDescription asChild className="mt-1"><div>{description}</div></DialogDescription>
                : <DialogDescription className="sr-only">{typeof title === 'string' ? title : 'Dialog'}</DialogDescription>}
            </div>
          </div>
        </DialogHeader>
        
        {/* Modal Body - Scrollable */}
        <div className="mt-4 overflow-y-auto flex-1 min-h-0 px-1">
          {children}
        </div>

        {/* Modal Footer — sticky to bottom on mobile */}
        {footer && (
          <div className="flex flex-row justify-end gap-2 pt-4 border-t mt-4 flex-shrink-0 sticky bottom-0 bg-white pb-2 sm:pb-0 sm:static">
            {footer}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StandardModal;

